import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import {
  ArrowLeft, Save, Store, Palette, MapPin, Clock, Globe,
  Truck, DollarSign, QrCode, Loader2, Megaphone, Receipt, MessageSquare
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { createContext, useContext } from "react";

type Restaurant = Tables<"restaurants">;

interface ConfigForm {
  name: string;
  description: string;
  phone: string;
  cnpj: string;
  primary_color: string;
  logo_url: string;
  cover_url: string;
  address_street: string;
  address_number: string;
  address_neighborhood: string;
  address_city: string;
  address_state: string;
  address_zip: string;
  opening_time: string;
  closing_time: string;
  delivery_fee: string;
  delivery_fee_per_km: string;
  delivery_free_until_km: string;
  delivery_radius_km: string;
  min_order_value: string;
  is_active: boolean;
  mp_access_token: string;
  mp_public_key: string;
  operating_days: string[];
  social_instagram: string;
  social_facebook: string;
  social_tiktok: string;
  social_website: string;
  ga_measurement_id: string;
  meta_pixel_id: string;
  is_manually_closed: boolean;
  menu_font: string;
  menu_bg_color: string;
  menu_text_color: string;
  menu_accent_color: string;
  menu_layout: string;
  menu_card_style: string;
  ai_combo_enabled: boolean;
  ai_combo_discount_percent: string;
  show_rating: boolean;
  scheduled_orders_enabled: boolean;
  split_payment_enabled: boolean;
  receipt_thank_you_message: string;
}

interface ConfigContextType {
  restaurant: Restaurant | null;
  form: ConfigForm;
  update: (field: string, value: any) => void;
  toast: ReturnType<typeof useToast>["toast"];
  uploadImage: (file: File, type: "logo" | "cover") => Promise<void>;
  uploadingLogo: boolean;
  uploadingCover: boolean;
  logoInputRef: React.RefObject<HTMLInputElement>;
  coverInputRef: React.RefObject<HTMLInputElement>;
}

export const ConfigContext = createContext<ConfigContextType>(null!);
export const useConfig = () => useContext(ConfigContext);

const menuItems = [
  { icon: Store, label: "Informações Gerais", path: "/configuracoes" },
  { icon: Palette, label: "Aparência", path: "/configuracoes/aparencia" },
  { icon: MapPin, label: "Endereço", path: "/configuracoes/endereco" },
  { icon: Clock, label: "Horário de Funcionamento", path: "/configuracoes/horario" },
  { icon: Globe, label: "Redes Sociais", path: "/configuracoes/redes-sociais" },
  { icon: Store, label: "Analytics & Pixel", path: "/configuracoes/analytics" },
  { icon: Truck, label: "Delivery", path: "/configuracoes/delivery" },
  { icon: DollarSign, label: "Método de Pagamento", path: "/configuracoes/pagamento" },
  { icon: QrCode, label: "QR Codes", path: "/configuracoes/qr-codes" },
  { icon: Megaphone, label: "Popups & Promoções", path: "/configuracoes/popups" },
  { icon: Receipt, label: "Faturamento", path: "/configuracoes/faturamento" },
  { icon: MessageSquare, label: "WhatsApp", path: "/configuracoes/whatsapp" },
];

const ConfiguracoesLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null!);
  const coverInputRef = useRef<HTMLInputElement>(null!);

  const [form, setForm] = useState<ConfigForm>({
    name: "",
    description: "",
    phone: "",
    cnpj: "",
    primary_color: "#f97316",
    logo_url: "",
    cover_url: "",
    address_street: "",
    address_number: "",
    address_neighborhood: "",
    address_city: "",
    address_state: "",
    address_zip: "",
    opening_time: "",
    closing_time: "",
    delivery_fee: "",
    delivery_fee_per_km: "",
    delivery_free_until_km: "",
    delivery_radius_km: "",
    min_order_value: "",
    is_active: true,
    mp_access_token: "",
    mp_public_key: "",
    operating_days: ["seg", "ter", "qua", "qui", "sex", "sab"],
    social_instagram: "",
    social_facebook: "",
    social_tiktok: "",
    social_website: "",
    ga_measurement_id: "",
    meta_pixel_id: "",
    is_manually_closed: false,
    menu_font: "Inter",
    menu_bg_color: "#ffffff",
    menu_text_color: "#222222",
    menu_accent_color: "",
    menu_layout: "list",
    menu_card_style: "rounded",
    ai_combo_enabled: false,
    ai_combo_discount_percent: "10",
    show_rating: false,
    scheduled_orders_enabled: false,
    split_payment_enabled: false,
    receipt_thank_you_message: "",
  });

  useEffect(() => {
    const load = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { navigate("/login"); return; }
      
      const impersonateId = sessionStorage.getItem("admin_impersonate_restaurant_id");
      let rest: any = null;
      
      if (impersonateId) {
        const { data } = await supabase
          .from("restaurants").select("*").eq("id", impersonateId).maybeSingle();
        rest = data;
      } else {
        const { data } = await supabase
          .from("restaurants").select("*").eq("owner_id", user.id).maybeSingle();
        rest = data;
      }
      
      if (!rest) { navigate("/dashboard"); return; }
      setRestaurant(rest);

      // Load payment credentials via SECURITY DEFINER function
      const { data: paymentCreds } = await supabase
        .rpc("get_restaurant_payment_credentials", { p_restaurant_id: rest.id })
        .maybeSingle();

      setForm({
        name: rest.name || "",
        description: rest.description || "",
        phone: rest.phone || "",
        cnpj: rest.cnpj || "",
        primary_color: rest.primary_color || "#f97316",
        logo_url: rest.logo_url || "",
        cover_url: rest.cover_url || "",
        address_street: rest.address_street || "",
        address_number: rest.address_number || "",
        address_neighborhood: rest.address_neighborhood || "",
        address_city: rest.address_city || "",
        address_state: rest.address_state || "",
        address_zip: rest.address_zip || "",
        opening_time: rest.opening_time?.slice(0, 5) || "",
        closing_time: rest.closing_time?.slice(0, 5) || "",
        delivery_fee: rest.delivery_fee?.toString() || "0",
        delivery_fee_per_km: (rest as any).delivery_fee_per_km?.toString() || "0",
        delivery_free_until_km: (rest as any).delivery_free_until_km?.toString() || "0",
        delivery_radius_km: rest.delivery_radius_km?.toString() || "5",
        min_order_value: rest.min_order_value?.toString() || "0",
        is_active: rest.is_active,
        mp_access_token: (paymentCreds as any)?.mp_access_token || "",
        mp_public_key: (paymentCreds as any)?.mp_public_key || "",
        operating_days: Array.isArray((rest as any).operating_days) ? (rest as any).operating_days : ["seg", "ter", "qua", "qui", "sex", "sab"],
        social_instagram: (rest as any).social_instagram || "",
        social_facebook: (rest as any).social_facebook || "",
        social_tiktok: (rest as any).social_tiktok || "",
        social_website: (rest as any).social_website || "",
        ga_measurement_id: (rest as any).ga_measurement_id || "",
        meta_pixel_id: (rest as any).meta_pixel_id || "",
        is_manually_closed: !!(rest as any).is_manually_closed,
        menu_font: (rest as any).menu_font || "Inter",
        menu_bg_color: (rest as any).menu_bg_color || "#ffffff",
        menu_text_color: (rest as any).menu_text_color || "#222222",
        menu_accent_color: (rest as any).menu_accent_color || "",
        menu_layout: (rest as any).menu_layout || "list",
        menu_card_style: (rest as any).menu_card_style || "rounded",
        ai_combo_enabled: !!(rest as any).ai_combo_enabled,
        ai_combo_discount_percent: (rest as any).ai_combo_discount_percent?.toString() || "10",
        show_rating: !!(rest as any).show_rating,
        scheduled_orders_enabled: !!(rest as any).scheduled_orders_enabled,
        split_payment_enabled: !!(rest as any).split_payment_enabled,
        receipt_thank_you_message: (rest as any).receipt_thank_you_message || "",
      });
      setLoading(false);
    };
    load();
  }, [navigate]);

  const update = (field: string, value: any) =>
    setForm((p) => ({ ...p, [field]: value }));

  const uploadImage = async (file: File, type: "logo" | "cover") => {
    if (!restaurant) return;
    const setUploading = type === "logo" ? setUploadingLogo : setUploadingCover;
    setUploading(true);
    try {
      const ext = file.name.split(".").pop() || "jpg";
      const path = `${restaurant.id}/${type}-${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("restaurant-images")
        .upload(path, file, { upsert: true });
      if (upErr) throw upErr;
      const { data: urlData } = supabase.storage
        .from("restaurant-images")
        .getPublicUrl(path);
      const field = type === "logo" ? "logo_url" : "cover_url";
      update(field, urlData.publicUrl);
      toast({ title: `${type === "logo" ? "Logo" : "Capa"} enviada com sucesso!` });
    } catch (err: any) {
      toast({ title: "Erro no upload", description: err.message, variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!restaurant) return;
    setSaving(true);
    try {
      // Check phone uniqueness before saving
      if (form.phone && form.phone !== restaurant.phone) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: isAvailable } = await supabase.rpc("is_phone_available", { 
            check_phone: form.phone,
            exclude_user_id: user.id
          });
          
          if (!isAvailable) {
            toast({ 
              title: "Telefone já cadastrado", 
              description: "Este número de telefone já está sendo usado por outro usuário.", 
              variant: "destructive" 
            });
            setSaving(false);
            return;
          }
        }
      }

      // Also update phone in profiles table
      if (form.phone !== restaurant.phone) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          await supabase.from("profiles")
            .update({ phone: form.phone || null })
            .eq("user_id", user.id);
        }
      }

      const { error } = await supabase.from("restaurants").update({
        name: form.name,
        description: form.description || null,
        phone: form.phone || null,
        cnpj: form.cnpj || null,
        primary_color: form.primary_color || null,
        logo_url: form.logo_url || null,
        cover_url: form.cover_url || null,
        address_street: form.address_street || null,
        address_number: form.address_number || null,
        address_neighborhood: form.address_neighborhood || null,
        address_city: form.address_city || null,
        address_state: form.address_state || null,
        address_zip: form.address_zip || null,
        opening_time: form.opening_time || null,
        closing_time: form.closing_time || null,
        delivery_fee: parseFloat(form.delivery_fee) || 0,
        delivery_fee_per_km: parseFloat(form.delivery_fee_per_km) || 0,
        delivery_free_until_km: parseFloat(form.delivery_free_until_km) || 0,
        delivery_radius_km: parseFloat(form.delivery_radius_km) || 5,
        min_order_value: parseFloat(form.min_order_value) || 0,
        is_active: form.is_active,
        // mp_access_token and mp_public_key moved to restaurant_payment_credentials
        operating_days: form.operating_days,
        social_instagram: form.social_instagram || null,
        social_facebook: form.social_facebook || null,
        social_tiktok: form.social_tiktok || null,
        social_website: form.social_website || null,
        ga_measurement_id: form.ga_measurement_id || null,
        meta_pixel_id: form.meta_pixel_id || null,
        is_manually_closed: form.is_manually_closed,
        menu_font: form.menu_font || "Inter",
        menu_bg_color: form.menu_bg_color || "#ffffff",
        menu_text_color: form.menu_text_color || "#222222",
        menu_accent_color: form.menu_accent_color || null,
        menu_layout: form.menu_layout || "list",
        menu_card_style: form.menu_card_style || "rounded",
        ai_combo_enabled: form.ai_combo_enabled,
        ai_combo_discount_percent: parseFloat(form.ai_combo_discount_percent) || 10,
        show_rating: form.show_rating,
        scheduled_orders_enabled: form.scheduled_orders_enabled,
        split_payment_enabled: form.split_payment_enabled,
        receipt_thank_you_message: form.receipt_thank_you_message || null,
      } as any).eq("id", restaurant.id);

      if (error) throw error;

      // Save payment credentials via SECURITY DEFINER function
      if (form.mp_access_token || form.mp_public_key) {
        await supabase.rpc("upsert_restaurant_payment_credentials", {
          p_restaurant_id: restaurant.id,
          p_mp_access_token: form.mp_access_token || null,
          p_mp_public_key: form.mp_public_key || null,
        });
      }

      toast({ title: "Configurações salvas com sucesso!" });
    } catch (err: any) {
      toast({ title: "Erro ao salvar", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <ConfigContext.Provider value={{ restaurant, form, update, toast, uploadImage, uploadingLogo, uploadingCover, logoInputRef, coverInputRef }}>
      <div className="min-h-screen bg-background flex flex-col">
        {/* Header */}
        <header className="sticky top-0 z-40 border-b border-border bg-card/95 backdrop-blur">
          <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <h1 className="font-display font-bold text-lg">Configurações</h1>
            </div>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-gradient-primary text-primary-foreground hover:opacity-90 gap-2"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Salvar
            </Button>
          </div>
        </header>

        <div className="flex-1 max-w-6xl mx-auto w-full flex">
          {/* Sidebar */}
          <nav className="w-64 shrink-0 border-r border-border bg-card p-3 space-y-1 hidden md:block">
            {menuItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                    isActive
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                  }`}
                >
                  <item.icon className="h-4 w-4 shrink-0" />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>

          {/* Mobile tabs */}
          <div className="md:hidden border-b border-border bg-card overflow-x-auto scrollbar-hide fixed top-14 left-0 right-0 z-30">
            <div className="flex gap-1 px-3 py-2">
              {menuItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                      isActive
                        ? "bg-primary text-primary-foreground"
                        : "bg-secondary text-muted-foreground"
                    }`}
                  >
                    <item.icon className="h-3.5 w-3.5" />
                    {item.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content */}
          <main className="flex-1 p-6 md:pt-6 pt-16 overflow-auto">
            <div className="max-w-2xl">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </ConfigContext.Provider>
  );
};

export default ConfiguracoesLayout;
